import { ref, computed } from 'vue';
import { LEVELLING_ANCHOR } from '../levelling/levellingMeta.js';

/**
 * Calibration (v3 homepage) — roles to review "in context" against their peers,
 * from `POST Report/calibration`. The backend returns factual signals (selected
 * vs suggested level, the function's peer count + average level position, an
 * anomaly flag); this composable derives the level text + the human note. `self`
 * pattern; caller injects an axios-like `http`. Navigation stays in presentation.
 *
 * Also states the CEO ceiling (PD-1258). Calibration is where someone decides
 * whether a level is defensible, and the ceiling is the framework's hard top — so
 * it is stated outright, and each row's DISTANCE to it is derived here from the
 * positions both the row and the ceiling already carry.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number} [options.getLimit] rows (default 8)
 * @param {() => void}   [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 100;

export function calibration(options) {
  const { http, getLimit = () => 8, onReady = () => {} } = options || {};
  const limit = () => getLimit() || 8;

  const self = { rows: ref([]), loading: ref(false), ceiling: ref(null) };

  self.isLoading = computed(() => self.loading.value);
  self.count = computed(() => self.rows.value.length);
  self.shownRows = computed(() => self.rows.value.slice(0, limit()));

  self.role = (r) => (r && (r.doc_title || r.doc_name || r.reference)) || '';
  self.isAnomaly = (r) => !!r && (r.is_anomaly === 1 || r.is_anomaly === true);
  /* Anchor roles (PD-1258). The endpoint orders anchors first, so they lead their
     band here as the calibration reference; these mark WHY they lead. Both flags
     are absent (falsy) for tenants without the feature. */
  self.isAnchor = (r) => !!r && (r.is_anchor === 1 || r.is_anchor === true);
  self.isCeiling = (r) => !!r && (r.is_ceiling === 1 || r.is_ceiling === true);
  // "5 peers" — the other roles in the same function (exclude this one).
  self.peers = (r) => Math.max(0, (Number(r && r.peer_count) || 0) - 1);

  /* ---- the ceiling, and the distance to it (PD-1258) ------------------------
     Positions, never level NAMES: a name is a client-configured string and isn't
     comparable across functions. Positive = levels below the ceiling, 0 = at it,
     negative = above it. Null whenever either side has no position — including a
     PENDING ceiling, which has no evaluation and so no level to measure against. */
  self.levelsBelowCeiling = (r) => {
    const ceil = self.ceiling.value;
    if (!ceil || ceil.levelPosition == null) return null;
    if (r == null || r.selected_position == null) return null;
    const pos = Number(r.selected_position);
    if (isNaN(pos)) return null;
    return Number(ceil.levelPosition) - pos;
  };

  /* Icon + label for the row's anchor mark, or null. The CEO ceiling is a DIFFERENT
     concept from an ordinary anchor (it fixes the framework's top), so it gets its
     own glyph rather than sharing the anchor one — otherwise the two are
     indistinguishable in a list. Names come from LEVELLING_ANCHOR so the control
     centre, compare and calibration all mark them identically. */
  self.anchorMark = (r) => {
    if (self.isCeiling(r)) return { ...LEVELLING_ANCHOR.ceiling, isCeiling: true };
    if (self.isAnchor(r)) return { ...LEVELLING_ANCHOR.anchor, isCeiling: false };
    return null;
  };

  /** "Chief Executive (E9)" / "Chief Executive (level pending)", or '' if none. */
  self.ceilingLabel = computed(() => {
    const c = self.ceiling.value;
    if (!c) return '';
    return `${c.title} (${c.pending || !c.levelCode ? 'level pending' : c.levelCode})`;
  });

  /** The statement of the framework top; '' when no ceiling is set. */
  self.ceilingText = computed(() =>
    (self.ceiling.value ? `Framework ceiling: ${self.ceilingLabel.value}` : ''));

  /* Per-row gap text, and ONLY for the cases worth reading: at, above, or one
     below. `rowSub` is already dense (anchor · note · peers · function), and a gap
     on every row would bury the signal this exists to surface — roles crowding the
     top of the framework. Callers wanting the raw number for every row have
     levelsBelowCeiling(). */
  self.ceilingGapText = (r) => {
    const gap = self.levelsBelowCeiling(r);
    if (gap == null || gap > 1) return '';
    if (gap < 0) return 'Above the framework ceiling';
    if (gap === 0) return 'At the framework ceiling';
    return '1 level below the ceiling';
  };

  const lvl = (name, pos) => {
    if (name && /^\d+$/.test(String(name).trim())) return 'L' + String(name).trim();
    if (name) return String(name);
    return pos != null ? 'L' + pos : '';
  };
  // Boundary case → "L3 / L4" (selected / suggested); otherwise the single level.
  self.levelText = (r) => {
    const sel = lvl(r && r.selected_level, r && r.selected_position);
    if (self.isAnomaly(r)) {
      const sug = lvl(r && r.suggested_level, r && r.suggested_position);
      return [sel, sug].filter(Boolean).join(' / ');
    }
    return sel;
  };
  // Why it needs calibration.
  self.note = (r) => {
    if (self.isAnomaly(r)) return 'On the boundary';
    const pos = Number(r && r.selected_position);
    const avg = Number(r && r.avg_position);
    if (!isNaN(pos) && !isNaN(avg)) {
      if (pos - avg >= 1) return 'Above the family average';
      if (avg - pos >= 1) return 'Below the family average';
    }
    return 'Awaiting calibration';
  };
  self.rowSub = (r) => {
    const p = self.peers(r);
    // An anchor leads with its role in the register, since that's why it's listed
    // first and what the reader needs to know before comparing anything to it.
    const anchor = self.isCeiling(r) ? 'CEO ceiling' : self.isAnchor(r) ? 'Anchor role' : '';
    /* The ceiling holder is not measured against itself — it IS the ceiling, so
       "At the framework ceiling" would be noise on that one row. */
    const gap = self.isCeiling(r) ? '' : self.ceilingGapText(r);
    return [anchor, gap, self.note(r), p ? `${p} peer${p === 1 ? '' : 's'}` : '', r && r.function_name]
      .filter(Boolean).join(' · ');
  };

  async function load() {
    self.loading.value = true;
    try {
      const resp = await http.post('Report/calibration', { limit: SERVER_LIMIT });
      const data = resp && resp.data;
      /* The endpoint now returns { rows, ceiling }; it used to return a bare row
         array, and may be wrapped in { Data } by the transport. Accept all three, so
         a portal deployed against an older API still lists rows rather than
         rendering empty — it just won't state the ceiling. */
      const payload = data && !Array.isArray(data) && data.Data !== undefined ? data.Data : data;
      if (Array.isArray(payload)) {
        self.rows.value = payload;
        self.ceiling.value = null;
      } else {
        self.rows.value = (payload && Array.isArray(payload.rows)) ? payload.rows : [];
        self.ceiling.value = (payload && payload.ceiling) || null;
      }
    } catch {
      self.rows.value = [];
      self.ceiling.value = null;
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
